<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="modal-overlay" @click.self="close">
        <div class="modal-container">
          <div class="modal-header">
            <h2 class="modal-title">个人设置</h2>
            <button class="modal-close" @click="close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <!-- 头像预览 -->
            <div class="form-group">
              <label class="form-label">头像</label>
              <div class="avatar-preview">
                <div class="avatar-current">
                  <img v-if="formData.avatar_url" :src="formData.avatar_url" alt="Avatar preview" />
                  <span v-else class="avatar-placeholder">{{ userNickname.charAt(0).toUpperCase() }}</span>
                </div>
                <div class="avatar-info">
                  <div class="avatar-email">{{ userEmail }}</div>
                  <div class="avatar-note">头像 URL，支持图片链接</div>
                </div>
              </div>
            </div>

            <!-- 头像 URL 输入 -->
            <div class="form-group">
              <label for="avatar_url" class="form-label">头像 URL</label>
              <input
                id="avatar_url"
                v-model="formData.avatar_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/avatar.png"
                @input="validateAvatarUrl"
              />
              <div v-if="errors.avatar_url" class="form-error">{{ errors.avatar_url }}</div>
              <div class="form-hint">留空则使用默认头像（昵称首字母）</div>
            </div>

            <!-- 昵称输入 -->
            <div class="form-group">
              <label for="nickname" class="form-label">昵称</label>
              <input
                id="nickname"
                v-model="formData.nickname"
                type="text"
                class="form-input"
                placeholder="输入昵称"
                maxlength="50"
                @input="validateNickname"
              />
              <div class="form-hint">
                {{ formData.nickname?.length || 0 }}/50 字符
              </div>
              <div v-if="errors.nickname" class="form-error">{{ errors.nickname }}</div>
            </div>

            <!-- 常用头像建议 -->
            <div class="form-group">
              <label class="form-label">快速设置头像</label>
              <div class="avatar-suggestions">
                <button
                  v-for="suggestion in avatarSuggestions"
                  :key="suggestion.url"
                  class="avatar-suggestion"
                  :class="{ active: formData.avatar_url === suggestion.url }"
                  @click="selectAvatar(suggestion.url)"
                >
                  <img :src="suggestion.url" :alt="suggestion.name" />
                  <span class="avatar-suggestion-name">{{ suggestion.name }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="close" :disabled="isLoading">
              取消
            </button>
            <button class="btn btn-primary" @click="save" :disabled="isLoading || !hasChanges">
              {{ isLoading ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import { useUserStore } from '../stores/user';
import { storeToRefs } from 'pinia';
import type { UpdateProfileInput } from '../api';

const userStore = useUserStore();
const { userEmail, userNickname, userAvatar } = storeToRefs(userStore);

const show = ref(false);
const isLoading = ref(false);

// 表单数据
const formData = reactive<UpdateProfileInput>({
  nickname: '',
  avatar_url: '',
});

// 错误信息
const errors = reactive<Record<string, string>>({
  nickname: '',
  avatar_url: '',
});

// 头像建议
const avatarSuggestions = [
  {
    name: '猫',
    url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=cat',
  },
  {
    name: '狗',
    url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=dog',
  },
  {
    name: '熊',
    url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=bear',
  },
  {
    name: '狐',
    url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=fox',
  },
];

// 原始数据（用于检测变化）
const originalData = reactive<UpdateProfileInput>({
  nickname: '',
  avatar_url: '',
});

// 检测是否有变化
const hasChanges = computed(() => {
  return (
    formData.nickname !== originalData.nickname ||
    formData.avatar_url !== originalData.avatar_url
  );
});

// 监听自定义事件打开模态框
window.addEventListener('open-user-settings', () => {
  open();
});

function open() {
  // 初始化表单数据
  formData.nickname = userNickname.value || '';
  formData.avatar_url = userAvatar.value || '';

  // 保存原始数据
  originalData.nickname = formData.nickname;
  originalData.avatar_url = formData.avatar_url;

  // 清除错误
  errors.nickname = '';
  errors.avatar_url = '';

  show.value = true;
}

function close() {
  show.value = false;
}

function validateNickname() {
  if (formData.nickname && formData.nickname.length > 50) {
    errors.nickname = '昵称不能超过 50 个字符';
    return false;
  }
  errors.nickname = '';
  return true;
}

function validateAvatarUrl() {
  if (formData.avatar_url) {
    try {
      new URL(formData.avatar_url);
      errors.avatar_url = '';
      return true;
    } catch {
      errors.avatar_url = '请输入有效的 URL';
      return false;
    }
  }
  errors.avatar_url = '';
  return true;
}

function selectAvatar(url: string) {
  formData.avatar_url = url;
  validateAvatarUrl();
}

async function save() {
  // 验证
  const isNicknameValid = validateNickname();
  const isAvatarUrlValid = validateAvatarUrl();

  if (!isNicknameValid || !isAvatarUrlValid) {
    return;
  }

  try {
    isLoading.value = true;
    const success = await userStore.updateProfile(formData);

    if (success) {
      close();
    }
  } finally {
    isLoading.value = false;
  }
}
</script>

<style>
/* Dark mode styles - global scope to work with .dark class */
.dark .modal-container {
  background: var(--bg-secondary) !important;
  border-color: var(--border-color) !important;
}

.dark .modal-header {
  border-bottom-color: var(--border-color) !important;
}

.dark .modal-title {
  color: var(--text-primary) !important;
}

.dark .modal-close {
  color: var(--text-secondary) !important;
}

.dark .modal-close:hover {
  background: var(--bg-tertiary) !important;
  color: var(--text-primary) !important;
}

.dark .modal-body {
  background: transparent;
}

.dark .modal-footer {
  border-top-color: var(--border-color) !important;
}

.dark .form-label {
  color: var(--text-secondary) !important;
}

.dark .form-input {
  background: var(--bg-secondary) !important;
  border-color: var(--border-color) !important;
  color: var(--text-primary) !important;
}

.dark .form-input:focus {
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
}

.dark .form-input::placeholder {
  color: var(--text-tertiary) !important;
}

.dark .form-hint {
  color: var(--text-tertiary) !important;
}

.dark .avatar-preview {
  background: var(--bg-tertiary) !important;
}

.dark .avatar-email {
  color: var(--text-primary) !important;
}

.dark .avatar-note {
  color: var(--text-secondary) !important;
}

.dark .avatar-suggestion {
  background: var(--bg-tertiary) !important;
}

.dark .avatar-suggestion:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.dark .avatar-suggestion-name {
  color: var(--text-secondary) !important;
}

.dark .btn-secondary {
  background: var(--bg-tertiary) !important;
  color: var(--text-primary) !important;
}

.dark .btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1) !important;
}
</style>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: var(--bg-card, #ffffff);
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid var(--border-color, #e5e5e5);
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text, #1a1a1a);
}

.modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--color-text-secondary, #666);
  cursor: pointer;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary, #f5f5f5);
  color: var(--color-text, #1a1a1a);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color, #e5e5e5);
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text, #1a1a1a);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 8px;
  background: var(--bg-card, #ffffff);
  color: var(--color-text, #1a1a1a);
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-input::placeholder {
  color: var(--color-text-tertiary, #999);
}

.form-hint {
  font-size: 12px;
  color: var(--color-text-tertiary, #999);
  margin-top: 6px;
}

.form-error {
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
}

.avatar-preview {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: var(--bg-secondary, #f5f5f5);
  border-radius: 12px;
}

.avatar-current {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-primary, #4f46e5);
  flex-shrink: 0;
}

.avatar-current img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;
  font-weight: 600;
  font-size: 24px;
}

.avatar-info {
  flex: 1;
}

.avatar-email {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text, #1a1a1a);
  margin-bottom: 4px;
}

.avatar-note {
  font-size: 12px;
  color: var(--color-text-secondary, #666);
}

.avatar-suggestions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.avatar-suggestion {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  background: var(--bg-secondary, #f5f5f5);
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.avatar-suggestion:hover {
  background: var(--bg-tertiary, #e5e5e5);
}

.avatar-suggestion.active {
  border-color: var(--color-primary, #4f46e5);
  background: rgba(79, 70, 229, 0.05);
}

.avatar-suggestion img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-suggestion-name {
  font-size: 11px;
  color: var(--color-text-secondary, #666);
}

.btn {
  flex: 1;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-primary, #4f46e5);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-dark, #4338ca);
}

.btn-secondary {
  background: var(--bg-secondary, #f5f5f5);
  color: var(--color-text, #1a1a1a);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary, #e5e5e5);
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
  opacity: 0;
}
</style>
